import React, {
  useState,
} from 'react';
import { InstanceStateData, LearningModuleStateData } from 'src/types/state/learningModule';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { useMetrics } from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import VideoView from './video';
import { DocumentView } from './pdf';

interface LearningModuleContentViewProps {
  learningModule: LearningModuleStateData;
  unauthenticated?: boolean;
  clientId?: string;
}

export interface LearningModuleViewProps {
  learningModule: LearningModuleStateData;
  moduleInstance: InstanceStateData;
  handleLanguageSelect: (language: string) => void;
  unauthenticated?: boolean;
  clientId?: string;
}

const LearningModuleContentView = ({ learningModule, unauthenticated, clientId }: LearningModuleContentViewProps) => {
  const { instanceMap, defaultLanguage } = learningModule;
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const selectedInstance: InstanceStateData | undefined = instanceMap[selectedLanguage];
  const { clickMetric } = useMetrics();

  if (!selectedInstance) {
    const errorMessage = `No instance found for selected language ${selectedLanguage} `
    + `in module with moduleId ${learningModule.moduleId}`;
    const error = new Error(errorMessage);
    ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
      moduleId: learningModule.moduleId,
      selectedLanguage,
      availableLanguages: Object.keys(instanceMap),
      url: window.location.href,
    });
    throw error;
  }

  const handleLanguageSelect = (language: string) => {
    clickMetric({
      feature: FeatureName.ModuleLanguageChange,
      metadata: {
        moduleId: learningModule.moduleId,
        previousLanguage: selectedLanguage,
        selectedLanguage: language,
        url: window.location.href,
      },
    });
    setSelectedLanguage(language);
  };

  const learningModuleViewBaseProps: LearningModuleViewProps = {
    learningModule,
    moduleInstance: selectedInstance,
    handleLanguageSelect,
    unauthenticated,
    clientId,
  };

  switch (selectedInstance.instanceType) {
    case 'VIDEO':
      return <VideoView {...learningModuleViewBaseProps} />;
    case 'DOCUMENT':
      return <DocumentView {...learningModuleViewBaseProps} />;
    default:
    {
      const typeErrorMessage = 'Unsupported learning module instance '
      + `type: ${selectedInstance.instanceType} for instanceId ${selectedInstance.instanceId}`;
      const error = new Error(typeErrorMessage);
      ClientLogger.error(ClientLog.IllegalInternalDataState, error, {
        moduleId: learningModule.moduleId,
        instanceId: selectedInstance.instanceId,
        instanceType: selectedInstance.instanceType,
        url: window.location.href,
      });
      throw error;
    }
  }
};

export default LearningModuleContentView;
